<!-- CommentsEditor -->
<script lang="ts" setup>
    import { reactive, ref, onMounted, computed, watch, nextTick } from 'vue';
    import '../../../css/choice.css';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import PageTitle from "../../components/PageTitle.vue"
    import { ICol, RenderModeType } from "../../lib/ChoiceTableHelpers" ;
    import { NoTzDate, getNoTzToday, prettyDate, getNoTzDateAdd, todayDateAndTime, addSeconds, toDisplayDateString } from '../../lib/DateUtils';
    import { CommentTypes, DatabaseIdTypes, ConstantDefaultCommentGroupsTypes, PrivilegeType, AppScreens, AppScreenNames, UserSettingGroup, 
        DateTimeFormatTypes, MessageChannelName, LegacyWindowActions, UserGroupType } from '../../Enums/ChoiceEnums';
    import { ButtonDisplaySize, MessageSectionType, DisplayStyle, AlignmentType, IconTypes, HoverCursorTypes, DateDisplayFormat } from '../../Enums/UiEnums';
    import { IDatabaseIdModel } from '../../lib/Models/DatabaseIdModel';
    import { FoldingPanel } from '../../LibMain';
    import { parsePrefixedIntOptions } from '../../lib/Parse/PrefixedInt';
    import { IExistingCommentModel, ISaveCommentReq, IDefaultCommentModel } from '../../lib/Api/Comments';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore'
    import { useUserSettingsStore } from '../../lib/Stores/UserSettingsStore'
    import { useCommentAttributesStore } from '../../lib/Stores/CommentAttributesStore';
    import { useListStore } from '../../lib/Stores/ListStore';
    import { initPinia } from '../../lib/PiniaMgr';
    import { IEntityInfoById } from '../../lib/Models/EntityModel';
    import { ModalWindowOpenByName } from '../../lib/ChoiceUserConfiguration';
    import { IAlertTreeOptionModel, constantAlertIds } from '../../lib/Models/Comments/Alerts';
    import { ISelection } from '../../lib/Models/Selection';
    import { IUserAndGroupTableItem } from '../../lib/Models/User/UserAndGroupItem';
    import { Debounce } from '../../lib/Time/Debounce';
    import * as Notification from "../../lib/Notification"
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import * as _ from 'lodash';
    import DropDownButton from "../../components/DropDownButton.vue";
    import DropDownButtonItem from "../../components/DropDownButtonItem.vue";
    import Card from "../../components/Card.vue"
    import KeyMonitor from "../../components/KeyMonitor.vue"
    import MessageBadge from "../../components/Messages/MessageBadge.vue"
    import TextInput from "../../components/TextInput.vue"
    import MessageSection from "../../components/Messages/MessageSection.vue"
    import DatePickerNoTZ from "../../components/DatePickerNoTZ.vue"
    import TitledRow from "../../components/TitledRow.vue"
    import ChoicePatientName from "../../components/ChoicePatientName.vue"
    import OptionsDropPanel from "../../components/OptionsDropPanel.vue"
    import SelectedUsersAndGroupBadges from "../../components/SelectionIndicators/SelectedUsersAndGroupBadges.vue"
    import Icon from '../../components/Icon.vue'
    import DefaultCommentsTable from './DefaultCommentsTable.vue';
    import SelectedAlertBadges from './SelectedAlertBadges.vue'
    import UserEntryDate from '../../components/UserEntryDate.vue';
    import ModalDialog from '../../components/ModalDialog.vue'
    import ModifierKeyMonitor from '../../components/ModifierKeyMonitor.vue'
    import UsersAndGroupsTable from './UsersAndGroupsTable.vue'
    import Privilege from '../../components/Privilege.vue'
    import StickyHeader from '../../components/StickyHeader.vue'
    import ChoiceStateMessageSink from '../../components/ChoiceStateMessageSink.vue';
    import AlertsTree from "../Comments/AlertsTree.vue"
    import LoadingSection from '../../components/LoadingSection.vue';
    import { ISearchUserGroupItem, ISearchUserGroupKey } from '../../lib/Models/SearchModel';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import { getCompanySetting } from '../../lib/Api/Claim';
    import { IntDatabaseIdAndNameModel } from '../../lib/Models/IntDatabaseIdAndNameModel';
    import vSelect from 'vue-select'; 
    import 'vue-select/dist/vue-select.css';
    
    const props = withDefaults(defineProps<{
        commentGroupId: string,
        noTitle?: string
    }>(), {
        // Default values
    });

    enum CommentTypeFilterOptions
    {
        All = "All",
        User = "User",
        System = "System"
    }

    interface CommentEditorPreferences
    {
        PatientEditing : { CommentTypeFilter: CommentTypeFilterOptions },
        ClaimEditing : { CommentTypeFilter: CommentTypeFilterOptions },
        ClaimProcedureEditing: { CommentTypeFilter: CommentTypeFilterOptions }
    }

    const compData = reactive<{
        parentId?: IDatabaseIdModel,
        existingComments:
        {
            data: IExistingCommentModel[],
            columns: ICol[],
            commentTypeListFilter: CommentTypeFilterOptions,
            openPanel:boolean
        },
        claimCommentCompanySettings:
        {
            followUpDateRequired?: string,
            billerActionRequired?: string,
            assignedToRequired?: string
        }
        commentEditorInfo:{
            show: boolean,
            originalCommentObject?: IExistingCommentModel,
            comment_id?: number,    // undefined: when adding a new comment
            entryDate?: NoTzDate,
            userName?: string,
            comment?: string,
            billerActionId?: number,
            followupDate?: NoTzDate,
            effectiveDate: NoTzDate,
            expirationDate?: NoTzDate,
            allUsersAndGroups: (IUserAndGroupTableItem & ISelection)[],    // Replace with compData.usersAndGroups
            alertOptionsPanelOpen: boolean,
            openAlertOptionsTree: boolean,
            selectedAlertOptions: number[],
            selectedUserOrGroupDatabaseIds?:IDatabaseIdModel[],
            savingComment?: boolean,
            assignedToUserOrGroup?: IDatabaseIdModel,
            billerActionCompleted?: boolean,
            billerActionCompletedDate?: NoTzDate,
            assignedToName?: string,
            usersAndGroupsList?: IntDatabaseIdAndNameModel[],
            selectedAssignedTo?: IntDatabaseIdAndNameModel,
            includeOnStatement?: boolean 
        },
        promptForMessageActionDialog:
        {
            show?: boolean,
            comment?: IDefaultCommentModel
        },
        usersAndGroups: IUserAndGroupTableItem[],
        commentFilters: CommentTypeFilterOptions[],
        billingActionOptions: ChoiceApi.IBillingActionOption[],
        defaultCommentsPanelOpen: boolean,
        currentConstantDefaultCommentGroupId?: number,
        patientEntity?  : IEntityInfoById,
        claim_id?: number,
        alertOptions: IAlertTreeOptionModel[],
        commentGrpInfo?: {claim_id?: number, patient_entity_id?: number, procedure_code?: string },
        commentEditorUserPreferences: CommentEditorPreferences,
        onMountedComplete?: boolean,
        legacyWindowAction?: string
    }>({
        existingComments:
        {
            data: [],
            columns:[
                { Name: '', ExtraData: { editButton: true }},
                { Name: 'Type', DataField: 'type'},
                { Name: 'User', DataField: 'system_user_name' },
                { Name: 'Date/Time', DataField: 'enter_date' },
                { Name: 'Comment', DataField: 'comment_text' }
            ],
            commentTypeListFilter: CommentTypeFilterOptions.All,
            openPanel: true
        },
        claimCommentCompanySettings:
        {
            followUpDateRequired: "false",
            billerActionRequired: "false",
            assignedToRequired: "false"
        },
        commentEditorInfo:
        {
            show: false,
            alertOptionsPanelOpen: false,
            effectiveDate: getNoTzToday(),
            allUsersAndGroups: [],
            openAlertOptionsTree: false,
            selectedAlertOptions: [],
            savingComment: false,
            billerActionCompleted: false,
            includeOnStatement: false,
        },
        promptForMessageActionDialog:{
            show: false
        },
        usersAndGroups: [],
        commentFilters: [CommentTypeFilterOptions.User, CommentTypeFilterOptions.System, CommentTypeFilterOptions.All],
        billingActionOptions: [],
        defaultCommentsPanelOpen: false,
        alertOptions: [],
        commentEditorUserPreferences: {
            PatientEditing : { CommentTypeFilter: CommentTypeFilterOptions.All },
            ClaimEditing : { CommentTypeFilter: CommentTypeFilterOptions.All },
            ClaimProcedureEditing: { CommentTypeFilter: CommentTypeFilterOptions.All }
        }
    });

    const commentTextInput = ref();
    const privilegesStore = usePrivilegesStore(initPinia());
    const userSettingsStore = useUserSettingsStore(initPinia());
    const commentAttributesStore = useCommentAttributesStore(initPinia());
    const listStore = useListStore(initPinia());

    onMounted(async () => {
        await wrapOnMounted();
        compData.onMountedComplete = true;
        updateCommentTypeFilterValue();

        commentAttributesStore.getAlertTree()
            .then(resp => {
                compData.alertOptions = resp;
            });
    });

    async function wrapOnMounted()
    {
        compData.parentId = getParentId();

        // Get user preferences
        userSettingsStore.getUserSettingObj<CommentEditorPreferences>(UserSettingGroup.CommentEditorSettings, "PREF")
            .then(respUserPref => {
                if (undefined != respUserPref)
                {
                    compData.commentEditorUserPreferences = respUserPref;
                    updateCommentTypeFilterValue();
                }
            });

        // Get rights
        await privilegesStore.getScreens([AppScreens.CommentScreen, AppScreens.DefaultComments, AppScreens.BillerAction]);

        const allowRead = await privilegesStore.allowRead(AppScreens.CommentScreen);
        if (!allowRead)
        {
            return;
        }
        
        switch (compData.parentId?.idType)
        {
            case DatabaseIdTypes.claim_id:
                compData.claim_id = <number>compData.parentId.id;
                break;

            case DatabaseIdTypes.comment_group_id:
                compData.commentGrpInfo = (await ChoiceApi.comments.getCommentGroupInfo(<number>compData.parentId.id));

                const commentGrpInfo = compData.commentGrpInfo;
                if (commentGrpInfo?.claim_id)
                    compData.claim_id = commentGrpInfo?.claim_id;
                if (commentGrpInfo?.patient_entity_id)
                {
                    ChoiceApi.entityInfo.getEntityInfoByDatabaseId({ id:commentGrpInfo?.patient_entity_id, idType: DatabaseIdTypes.patient_entity_id })
                        .then(resp => compData.patientEntity = resp);
                }
                break;


            case DatabaseIdTypes.patient_id:
            case DatabaseIdTypes.patient_entity_id:
                break;

            default:
            alert(`Unexpected parent ID type: ${compData.parentId?.idType}`);
            return;
        }

        compData.existingComments.data = await ChoiceApi.comments.groupComments(compData.parentId.idType, <number>compData.parentId.id);

        if (compData.claim_id)
        {
            compData.currentConstantDefaultCommentGroupId = ConstantDefaultCommentGroupsTypes.ClaimCharge;
            
            //add the selectedAssignedTo property to our existing comments array
            compData.existingComments.data = await addAssignedTo(compData.existingComments.data);

            ChoiceApi.entityInfo.getEntityInfoByDatabaseId({ id:compData.claim_id, idType: DatabaseIdTypes.claim_id })
                .then(resp => { 
                    compData.patientEntity = resp; 
                    updateCommentTypeFilterValue(); 
                });
            
            //Load Company Settings for Claim Comments    
            await loadClaimCommentCompanySettings();

            //Get user/group list to use for Assigned To selection
            compData.commentEditorInfo.usersAndGroupsList = await ChoiceApi.list.getAllUsersAndGroups();

            // Get info for claim comments
            ChoiceApi.billingActions.options()
                .then(resp => { 
                    compData.billingActionOptions = resp;
                    updateCommentTypeFilterValue(); 
                 });
            return;
        }

        if (isPatientComment.value)
            await onMountedPatientComment();
        
    }  

    async function addAssignedTo(commentsData: IExistingCommentModel[]) {
        //loop over our existing comments array and add in our selectedAssignedTo property
        const assignedPromises = commentsData.map(async (item) => {
            const name = await getAssignedToName(item.assignedToIdType, item.assignedToId);

            return {
                ...item,
                selectedAssignedTo: {
                    idType: item.assignedToIdType ?? DatabaseIdTypes.security_users_id,
                    id: typeof item.assignedToId === 'number' ? item.assignedToId : 0,
                    name: name ?? ''
                }
            };
        });

        return await Promise.all(assignedPromises);
    }
    
    async function getAssignedToName(assignedToIdType?: DatabaseIdTypes, assignedToId?: string | number) {
        //if we're passing in a User
        if (assignedToIdType === DatabaseIdTypes.security_users_id && (typeof assignedToId === 'number')) {
            const userResult = await ChoiceApi.comments.getSecurityUserIdInfo(assignedToId);

            if (userResult) {
                return userResult.user_name;
            }
        };

        //if we're passing in a Group
        if (assignedToIdType === DatabaseIdTypes.user_group_id && (typeof assignedToId === 'number')) {
            const groupResult = await ChoiceApi.comments.getUserGroupIdInfo(assignedToId);

            if (groupResult) {
                return groupResult.description;
            }
        };

        return undefined;
    }
        
    // Get data needed for patient comments
    async function onMountedPatientComment()
    {
        if (!compData.parentId || !compData.parentId.idType)
        {
            alert("parentId not specified");
            return;
        }

        // Get patient entity info
        switch (compData.parentId.idType)
        {
            case DatabaseIdTypes.patient_id:
            case DatabaseIdTypes.patient_entity_id:
            compData.patientEntity = await ChoiceApi.entityInfo.getEntityInfoByDatabaseId(compData.parentId);
            break;

            case DatabaseIdTypes.comment_group_id:
            if (compData.commentGrpInfo?.patient_entity_id)
            {
                compData.patientEntity = await ChoiceApi.entityInfo.getEntityInfoByDatabaseId( { id: compData.commentGrpInfo?.patient_entity_id, idType: DatabaseIdTypes.patient_entity_id } );
            }
            break;

            default:
            alert(`Unexpected parentId: ${compData.parentId.idType ?? 'none'}`);
            return;    
        }
        
        if (!compData.patientEntity)
        {
            alert("Issue getting patient info");
            return;
        }

        listStore
            .getUsersGroupsAndCommentFavoritesArray()
            .then(resp => compData.usersAndGroups = resp);

        compData.currentConstantDefaultCommentGroupId = ConstantDefaultCommentGroupsTypes.Patient;
    }

    function getParentId(): IDatabaseIdModel | undefined
    {
        const idValue = parsePrefixedIntOptions(props.commentGroupId, [
            { prefixText: "PEID", idType: DatabaseIdTypes.patient_entity_id },
            { prefixText: "claim", idType: DatabaseIdTypes.claim_id },
            { prefixText: "chart", idType: DatabaseIdTypes.patient_id }
        ]);

        if (idValue)
            return idValue;

        if (parseInt(props.commentGroupId))
            return { idType: DatabaseIdTypes.comment_group_id, id: props.commentGroupId };
    };

    const isFollowupDateRequired = computed<boolean>(() => "true" === compData.claimCommentCompanySettings.followUpDateRequired);
    const isBillerActionRequired = computed<boolean>(() => "true" === compData.claimCommentCompanySettings.billerActionRequired);
    const isAssignedToRequired = computed<boolean>(() => "true" === compData.claimCommentCompanySettings.assignedToRequired);
    const displayTitle = computed<boolean>(() => "true" != props.noTitle);
    const existingCommentsClass = computed<string>(() => "true" != props.noTitle ? "mb-1" : "mb-1 mt-3");

    const followupDateReqsMet = computed<boolean>(() => {
        if (!isFollowupDateRequired.value) {
            return true;
        }
        if (isFollowupDateRequired.value && ((compData.commentEditorInfo.followupDate?.trim()?.length ?? 0) > 0)) {
            return true;
        }
        return false;
    });

    const billerActionReqsMet = computed<boolean>(() => {
        if (!isBillerActionRequired.value) {
            return true;
        }
        if (isBillerActionRequired.value && (compData.commentEditorInfo.billerActionId)) {
            return true;
        }
        return false;
    });

    const assignedToReqsMet = computed<boolean>(() => {
        if (!isAssignedToRequired.value) {
            return true;
        }
        if (isAssignedToRequired.value && (compData.commentEditorInfo.selectedAssignedTo)) {
            return true;
        }        
        return false;
    });

    const followUpLabel = computed<string>(() => isFollowupDateRequired.value ? "*Followup Date" : "Followup Date"); 
    const billerActionLabel = computed<string>(() => isBillerActionRequired.value ? "*Biller Action" : "Biller Action"); 
    const assignedToLabel = computed<string>(() => isAssignedToRequired.value ? "*Assigned To" : "Assigned To");   

    const validNewComment = computed<boolean>(() => 
        ((compData.commentEditorInfo.comment?.trim()?.length ?? 0) > 0) &&
        (followupDateReqsMet.value) &&
        (billerActionReqsMet.value) && 
        (assignedToReqsMet.value));

    async function saveComment()
    {
        // Avoid another message from being handled while still saving a comment
        if (saveMessageDeBounce.isBlocked())
        {
            Notification.popAlert("Already saving comment");
            return;
        }        

        compData.commentEditorInfo.savingComment = true;
        saveCommentAndPreventDuplication();
        compData.commentEditorInfo.savingComment = false;
        saveMessageDeBounce.reset();
    }
    const saveMessageDeBounce = new Debounce(30);

    // Prevent a new comment from being added more than once
    async function saveCommentAndPreventDuplication()
    {
        if (!compData.commentEditorInfo.show)
            return;

        if (isSystemComment.value)
        {
            Notification.popAlert("You cannot overwrite system comments");
            return;
        }

        if (!compData.commentEditorInfo.comment?.trim())        
        {
            Notification.popAlert("Please enter a (valid) comment");
            return;
        }

        if (!compData.parentId)
        {
            Notification.popAlert("compData.parentId is undefined");
            return;
        }

        if (!validNewComment.value) {
            Notification.popAlert("Please enter the designated required fields");
            return;
        }

        const saveCommentData: ISaveCommentReq = {
            parentId: compData.parentId,
            comment_id: compData.commentEditorInfo.comment_id,
            comment_text: compData.commentEditorInfo.comment,
            comment_type: CommentTypes.User,
            //enter_date: getNoTzToday(),
            enter_date: prettyDate(Date.now()) ?? "",
            has_alert: compData.commentEditorInfo.selectedAlertOptions.length > 0,
        };
        if (saveCommentData.has_alert)
        {
            saveCommentData.alert_effective_date = compData.commentEditorInfo.effectiveDate;
            saveCommentData.alert_expiration_date = compData.commentEditorInfo.expirationDate;
            saveCommentData.alertTypeIds = compData.commentEditorInfo.selectedAlertOptions;
            saveCommentData.userOrGroupList = compData.commentEditorInfo.selectedUserOrGroupDatabaseIds;
        }
        if (isClaimComment.value || isProcedureComment.value)
        {
            saveCommentData.followup_date = compData.commentEditorInfo.followupDate;
            saveCommentData.biller_action_id = compData.commentEditorInfo.billerActionId;
            saveCommentData.assignedToId = compData.commentEditorInfo.selectedAssignedTo?.id;
            saveCommentData.assignedToIdType = DatabaseIdTypes.user_id == compData.commentEditorInfo.selectedAssignedTo?.idType ? DatabaseIdTypes.security_users_id : compData.commentEditorInfo.selectedAssignedTo?.idType;
            saveCommentData.billerActionCompleted = compData.commentEditorInfo.billerActionCompleted;
        }
        if ((isPatientComment.value) || (isClaimComment.value))
        {
            saveCommentData.includeOnStatement = compData.commentEditorInfo.includeOnStatement;
        }

        // Get the local user timezone
        const respTZ = await ChoiceApi.user.getUserTimeZoneInfo({dateFormatIndicator: DateTimeFormatTypes.AMPM, dateToFormat: new Date()});
        if (!respTZ)
        {
            Notification.popAlert("Error trying to get user time zone info");
            return;
        }

        // Save the comment
        const resp = await ChoiceApi.comments.save(saveCommentData);
        if (!resp)
        {
            Notification.popAlert("Error trying to save comment");
            return;
        }

        const existingComment: IExistingCommentModel = 
        {
                comment_group_id: resp.comment_group_id,
                comment_id: resp.comment_id,
                comment_text: compData.commentEditorInfo.comment,
                type: "User",
                alert: compData.commentEditorInfo.selectedAlertOptions.length > 0,
                enter_date:  respTZ?.formattedDateWithTimeZone ?? todayDateAndTime(),    
                alert_effective_date: compData.commentEditorInfo.effectiveDate,
                alert_expiration_date: compData.commentEditorInfo.expirationDate,
                system_user_name: resp.user_id,
                followup_date: compData.commentEditorInfo.followupDate,
                biller_action_id: compData.commentEditorInfo.billerActionId,
                userOrGroupList: compData.commentEditorInfo.selectedUserOrGroupDatabaseIds,
                assignedToId: compData.commentEditorInfo.assignedToUserOrGroup?.id,
                assignedToIdType: compData.commentEditorInfo.assignedToUserOrGroup?.idType,
                billerActionCompleted: compData.commentEditorInfo.billerActionCompleted,
                billerActionCompletedDate: compData.commentEditorInfo.billerActionCompletedDate,
                selectedAssignedTo: compData.commentEditorInfo.selectedAssignedTo,
                includeOnStatement: compData.commentEditorInfo.includeOnStatement
        };

        if (isNewComment.value)
        {
            compData.existingComments.data.push(existingComment);

            //re-order the data
            compData.existingComments.data = _.orderBy(compData.existingComments.data, ["comment_id"], 'desc');
        }
        else
        {
            // Update the original comment entry
            const commentIndex = compData.existingComments.data.findIndex(i => i.comment_id === compData.commentEditorInfo.comment_id);
            compData.existingComments.data[commentIndex] = existingComment;
        }

        compData.commentEditorInfo.comment = undefined;
        compData.commentEditorInfo.alertOptionsPanelOpen = false;
        compData.commentEditorInfo.show = false;

        compData.existingComments.openPanel = true;        
    }

    const compTitle = computed<string>(() => {

        if (!compData.parentId)
            return "";

        switch (compData.parentId.idType) {
            case DatabaseIdTypes.comment_group_id:
                {
                    if (compData.commentGrpInfo?.procedure_code)
                        return "Claim Procedure Comments";
    
                    if (compData.commentGrpInfo?.claim_id)
                        return "Claim Comments";
    
                    return "Patient Comments";
                }

            case DatabaseIdTypes.patient_entity_id:
            case DatabaseIdTypes.patient_id:
            return "Patient Comments";

            case DatabaseIdTypes.claim_id:
            return "Claim Comments";

            default:
                alert(`Unhandled parent id type: ${compData.parentId?.idType}`);
                return "???";
        }
    });

    async function addSelectedDefaultComment(defaultComment?: IDefaultCommentModel, replaceExistingComment?: boolean)
    {
        if (!defaultComment)
            return;

        if (undefined === replaceExistingComment)
        {
            askUserHowToUseMessage(defaultComment);
            return;
        }

        if (true === replaceExistingComment)
        {
            compData.commentEditorInfo.comment = defaultComment.commentText;
            compData.defaultCommentsPanelOpen = false;

            commentTextInput.value.focus();
            commentTextInput.value.scrollIntoView({ block: "nearest"});
        }
        else
        {
            if ((compData.commentEditorInfo.comment?.length ?? 0) > 0)
                compData.commentEditorInfo.comment += " ";
            else
                compData.commentEditorInfo.comment = "";
            
            compData.commentEditorInfo.comment += defaultComment.commentText;
        }


        // Update the biller action if it hasn't been set yet        
        if (!haveCommentAlertsSelected.value || replaceExistingComment)
        {
            if (replaceExistingComment)
                compData.commentEditorInfo.alertOptionsPanelOpen = false;

            if (isClaimComment.value || isProcedureComment.value)
            {
                if (replaceExistingComment)
                    compData.commentEditorInfo.billerActionId = undefined;

                if (!compData.commentEditorInfo.billerActionId)
                {
                    if (defaultComment.biller_action_id)
                    {
                        compData.commentEditorInfo.billerActionId = defaultComment.biller_action_id;
                        compData.commentEditorInfo.alertOptionsPanelOpen = true;
                    }
                }
            }
    
            // Update the alert/audience if it hasn't been set yet
            if (isPatientComment.value)
            {
                if (replaceExistingComment)
                {
                    compData.commentEditorInfo.selectedAlertOptions = [];
                    compData.commentEditorInfo.selectedUserOrGroupDatabaseIds = [];
                }

                if (defaultComment.alert_types.length > 0)
                {
                    compData.commentEditorInfo.selectedAlertOptions = defaultComment.alert_types;
                    compData.commentEditorInfo.alertOptionsPanelOpen = true;
                }
    
                if ((defaultComment.audienceIds?.length ?? 0) > 0)
                {
                    compData.commentEditorInfo.selectedUserOrGroupDatabaseIds = defaultComment.audienceIds;
                    compData.commentEditorInfo.alertOptionsPanelOpen = true;
                }
            }
        }
    }

    function askUserHowToUseMessage(comment: IDefaultCommentModel)
    {
        compData.promptForMessageActionDialog.comment = comment;
        compData.promptForMessageActionDialog.show = true;
    }

    // Get the alert option tree item based on the specified alert ID
    function getAlertOptionTreeItem(alertId: number): IAlertTreeOptionModel|undefined
    {
        for(const item of compData.alertOptions)
        {
            if (item.id === alertId)
                return item;

            if (item.children)
            {
                const match = item.children.find(i => i.id === alertId);
                if (match)
                    return match;
            }
        }
    }

    // Reset alert tree items so nothing is selected
    function resetAlertTree()
    {
        compData.commentEditorInfo.selectedAlertOptions = [];
    }

    async function onShowNewCommentSection()
    {
        compData.defaultCommentsPanelOpen = false;
        compData.existingComments.openPanel = false;
        compData.commentEditorInfo.originalCommentObject = undefined;
        compData.commentEditorInfo.comment_id = undefined;
        compData.commentEditorInfo.entryDate = undefined;
        compData.commentEditorInfo.comment = undefined;
        compData.commentEditorInfo.billerActionId = undefined;
        compData.commentEditorInfo.followupDate = undefined;
        compData.commentEditorInfo.selectedAssignedTo = undefined;
        compData.commentEditorInfo.effectiveDate = getNoTzToday();
        compData.commentEditorInfo.expirationDate = undefined;
        compData.commentEditorInfo.selectedUserOrGroupDatabaseIds = [];
        compData.commentEditorInfo.openAlertOptionsTree = true;
        compData.commentEditorInfo.includeOnStatement = false;
        compData.commentEditorInfo.show = true; 
        resetAlertTree();

        await nextTick();
        commentTextInput.value.focus();
        commentTextInput.value.scrollIntoView({ block: "nearest"});
    }

    const visibleExistingComments = computed<IExistingCommentModel[]>(() => {
        if (CommentTypeFilterOptions.All === (compData.existingComments.commentTypeListFilter ?? CommentTypeFilterOptions.All))
            return compData.existingComments.data;
        return compData.existingComments.data
                .filter(item => item.type === compData.existingComments.commentTypeListFilter);
    });

    const isPatientComment = computed(()=>{
        switch(compData.parentId?.idType)
        {
            case DatabaseIdTypes.patient_entity_id:
            case DatabaseIdTypes.patient_id:
                return true;
            case DatabaseIdTypes.comment_group_id:
                if (!compData.commentGrpInfo?.procedure_code
                && !compData.commentGrpInfo?.claim_id)
                    return true;
                break;   
            case DatabaseIdTypes.claim_id:
                return false;
            default:
                alert(`Unhandled parent ID type: ${compData.parentId?.idType} (isPatientComment)`);
                break;
        }
        return false;
    });
    const isProcedureComment = computed(()=>{ 
        switch(compData.parentId?.idType)
        {
            case DatabaseIdTypes.comment_group_id:
                if ((compData.commentGrpInfo?.claim_id) && (compData.commentGrpInfo?.procedure_code?.length))
                    return true; 
                break;
            case DatabaseIdTypes.claim_id:
                return false; 
            case DatabaseIdTypes.patient_entity_id:
            case DatabaseIdTypes.patient_id:
                return false;  
            default:
                alert(`Unhandled parent ID type: ${compData.parentId?.idType} (isProcedureComment)`);
                break;                
        }
        return false;            
    });

    const isClaimComment = computed(()=>{
        switch(compData.parentId?.idType)
        {
            case DatabaseIdTypes.claim_id:
                return true; 
            case DatabaseIdTypes.comment_group_id:
                if (compData.commentGrpInfo?.procedure_code?.length)
                    return false;
                if (compData.commentGrpInfo?.claim_id)
                    return true;  
                break;
            case DatabaseIdTypes.patient_entity_id:
            case DatabaseIdTypes.patient_id:
                return false;  
            default:
                alert(`Unhandled parent ID type: ${compData.parentId?.idType} (isClaimComment)`);
                break;                
        }
        return false;            
    });

     // Prepare to edit an existing comment
    async function onEditComment(comment: IExistingCommentModel)
    {
        compData.defaultCommentsPanelOpen = false;
        
        const editor = compData.commentEditorInfo;
        editor.show = true;
        await nextTick();
        
        editor.originalCommentObject = comment;

        editor.comment_id = comment.comment_id;
        editor.entryDate = comment.enter_date;
        editor.userName = comment.system_user_name;
        editor.comment = comment.comment_text;
        editor.billerActionId = comment.biller_action_id;
        editor.selectedAssignedTo = comment.selectedAssignedTo;

        editor.openAlertOptionsTree = false;

        editor.alertOptionsPanelOpen = comment.alert;
        if (comment.alert)
        {
            await nextTick();
            editor.effectiveDate = comment.alert_effective_date ?? getNoTzToday();
            editor.expirationDate = comment.alert_expiration_date;
        }

        // Claim or Procedure comment
        if (isClaimComment.value || isProcedureComment.value)
        {
            editor.followupDate = comment.followup_date;
            editor.assignedToUserOrGroup = { id: comment.assignedToId ?? 0, idType: comment.assignedToIdType ?? DatabaseIdTypes.security_users_id }
            editor.billerActionCompleted = comment.billerActionCompleted;
            editor.billerActionCompletedDate = (!comment.billerActionCompletedDate && comment.billerActionCompleted) ? getNoTzToday() : comment.billerActionCompletedDate;
            if (isClaimComment.value) {
                editor.includeOnStatement = comment.includeOnStatement;
            }
        }
        
        // Patient comment
        if (isPatientComment.value)
        {
            compData.commentEditorInfo.selectedUserOrGroupDatabaseIds = comment.userOrGroupList;
            compData.commentEditorInfo.includeOnStatement = comment.includeOnStatement;
            // Clear the alert tree selections
            resetAlertTree();
                        
            // Get comment alerts
            const alertIds = await ChoiceApi.comments.getAlerts(comment.comment_id);
            if (!alertIds)
            {
                Notification.popAlert("Error getting comment alerts");
                return;
            }
            if (alertIds.length)
            {
                editor.openAlertOptionsTree = true;
                await nextTick();

                for(const alertId of alertIds)
                {
                    const alertTreeItem = getAlertOptionTreeItem(alertId);
                    if (!alertTreeItem)
                    {
                        alert("Unable to find matching alert option in tree");
                        continue;
                    }
                    alertTreeItem.selected = true;

                    // If a lower level item, open the parent
                    if (alertTreeItem.parent)
                    {
                        alertTreeItem.parent.showChildItems = true;
                    }

                    // Update selection collection
                    compData.commentEditorInfo.selectedAlertOptions.push(alertId);
                }
            }
        }
        compData.existingComments.openPanel = false;
    }

    function showExistingComments()
    {
        compData.existingComments.openPanel = true;
        compData.commentEditorInfo.show = false;
        compData.commentEditorInfo.comment = undefined;
        compData.commentEditorInfo.comment_id = undefined;
    }

    function monitorKeyDown(event: KeyboardEvent)
    {
        if ("Escape" === event.key)
            showExistingComments();

        if (event.ctrlKey && "KeyS" === event.code)
        {
            event.preventDefault();
            saveComment();
        }
    }

    function userAndGroupCount(comment: IExistingCommentModel): {userCount?: number, userGroupCount?: number} | undefined
    {
        if (0 === (comment.userOrGroupList?.length ?? 0))
            return;

        return {
            userCount: comment.userOrGroupList?.filter(i => i.idType === DatabaseIdTypes.user_id).length,
            userGroupCount: comment.userOrGroupList?.filter(i => i.idType === DatabaseIdTypes.user_group_id).length,
        };
    }

    function billerActionName(comment: IExistingCommentModel): string | undefined {
        const action = compData.billingActionOptions.find(i => i.biller_action_id === comment.biller_action_id)
        return action?.description;
    }

    const enableCommentEditing = computed(() => 
        privilegesStore.screens[AppScreens.CommentScreen] >= PrivilegeType.ReadUpdate && true !== isSystemComment.value);

    const isNewComment = computed(() => !compData.commentEditorInfo.comment_id);

    const commentCardTitle = computed(() => isNewComment.value ? "New Comment" : "Edit Comment");

    const addNewCommentProblemText = computed(() => !validNewComment ? "Please enter a comment and/or required fields" : undefined);

    const isSystemComment = computed(() => "System" === compData.commentEditorInfo.originalCommentObject?.type);

    function updateCommentTypeFilterValue()
    {
        nextTick(() =>{
            const filterVal = commentTypeFilterValue();
            compData.existingComments.commentTypeListFilter = filterVal;
        });
    }

    function commentTypeFilterValue()
    {
        if (!compData.onMountedComplete)
            return CommentTypeFilterOptions.All;
        
        if (isPatientComment.value)
            return compData.commentEditorUserPreferences.PatientEditing.CommentTypeFilter;
        else if (isProcedureComment)
            return compData.commentEditorUserPreferences.ClaimProcedureEditing.CommentTypeFilter;
        else if (isClaimComment.value)
            return compData.commentEditorUserPreferences.ClaimEditing.CommentTypeFilter;
    
        return CommentTypeFilterOptions.All;
    }

    async function saveCommentTypeFilterValue(value: CommentTypeFilterOptions)
    {
        if (isPatientComment.value)
            compData.commentEditorUserPreferences.PatientEditing.CommentTypeFilter = value;
        else if (isProcedureComment)
            compData.commentEditorUserPreferences.ClaimProcedureEditing.CommentTypeFilter = value;
        else if (isClaimComment.value)
            compData.commentEditorUserPreferences.ClaimEditing.CommentTypeFilter = value;

        await userSettingsStore.saveUserSetting(UserSettingGroup.CommentEditorSettings, "PREF", compData.commentEditorUserPreferences)
        updateCommentTypeFilterValue();
    }

    function ChangeBillerAction()
    {
        // For new comments, update the follow-up date?
        if (!compData.commentEditorInfo.comment_id)
        {
            const newBillerAction = compData.billingActionOptions.find(act => act.biller_action_id === compData.commentEditorInfo.billerActionId);
            if (newBillerAction?.action_days)
                compData.commentEditorInfo.followupDate = getNoTzDateAdd(newBillerAction?.action_days);
            else
                compData.commentEditorInfo.followupDate = undefined;
        }
    }

    const visibleBillingActionOptions = computed(() => {
        if (isNewComment.value)
            return compData.billingActionOptions.filter(act => act.active);

        // Show biller action even if in-active
        return compData.billingActionOptions.filter(act => act.active || act.biller_action_id === compData.commentEditorInfo.billerActionId);
    });

    async function showBillerActionEditor()
    {
        await ModalWindowOpenByName(AppScreens.BillerAction);
        compData.billingActionOptions = await ChoiceApi.billingActions.options();
        updateCommentTypeFilterValue(); 
    }

    function sinkCommentActionMessage(event: MessageEvent) {
        //Added a broadcast channel to PatientPortal_Comments.htm for the CommentsEditor that will return what legacy window button was clicked (like New or Save)
        compData.legacyWindowAction = event.data.legacy_window_action;

        if (LegacyWindowActions.New === compData.legacyWindowAction) {
            onShowNewCommentSection();
        }

        if (LegacyWindowActions.Save === compData.legacyWindowAction) {

            // Are we editing a comment?
            if (!compData.commentEditorInfo.show)
                return;
            saveComment();
        }
    }

    function commentTypeCount(commentType: CommentTypeFilterOptions): number
    {
        if (commentType === CommentTypeFilterOptions.All)
            return compData.existingComments.data.length;
        return _.filter(compData.existingComments.data, item => item.type === commentType).length;
    }

    const haveCommentAlertsSelected = computed<boolean>(() =>{
        if (isClaimComment.value || isProcedureComment.value)
        {
            return !!compData.commentEditorInfo.billerActionId
        }
        if (isPatientComment.value)
        {
            return compData.commentEditorInfo.selectedAlertOptions.length > 0
                    || (compData.commentEditorInfo.selectedUserOrGroupDatabaseIds?.length ?? 0) > 0;
        }
        return false;
    });

    async function loadClaimCommentCompanySettings() {
        const paramName = "Claim";
        compData.claimCommentCompanySettings.followUpDateRequired = await getCompanySetting(paramName,36);
        compData.claimCommentCompanySettings.billerActionRequired = await getCompanySetting(paramName,37);
        compData.claimCommentCompanySettings.assignedToRequired = await getCompanySetting(paramName,38);
    }

</script>


<template>
<div style="overflow-y: auto;">
    <KeyMonitor @keydown="monitorKeyDown" />
    <PageTitle v-if="displayTitle" >
        <template v-slot:title>
            {{ compTitle }}
        </template>
        <template v-slot:subtitle>
            <div v-if="compData.claim_id">
                <Icon :icon="IconTypes.Tag" :display-style="DisplayStyle.Secondary"/>
                Claim: {{ compData.claim_id }}
                <span v-if="compData.commentGrpInfo?.procedure_code" class="ms-2">
                    <Icon :icon="IconTypes.Tag" :display-style="DisplayStyle.Secondary"/>
                    Procedure Code: {{ compData.commentGrpInfo?.procedure_code }}
                </span>
            </div>
            
            <div v-if="compData.patientEntity">
                <Icon :icon="IconTypes.User" :display-style="DisplayStyle.Secondary" class="me-1"/>
                <ChoicePatientName :patientEntity="compData.patientEntity" />
            </div>
        </template>
    </PageTitle>

    <FoldingPanel v-model:panelOpen="compData.existingComments.openPanel" :class="existingCommentsClass" v-cloak>
        <template #title>
            Existing Comments
            <MessageBadge :displayStyle="DisplayStyle.Info" v-if="compData.existingComments.data.length > 0">
                {{compData.existingComments.data.length}}
            </MessageBadge>
        </template>
        <template #body>
            <div class="row">
                <Privilege :screen="AppScreens.CommentScreen" :privilege="PrivilegeType.ReadUpdateWrite">
                <div class="col-sm-2 pb-2">
                    <button class="btn btn-primary btn-sm" title="Add new comment" @click="onShowNewCommentSection()">
                        <Icon :icon="IconTypes.Add" :displayStyle="DisplayStyle.Light" />
                    </button>
                </div>    
            </Privilege>
                <div class="col-sm-10">
                    <div class="float-end mb-1">
                        Comment Type
    
                        <DropDownButton :size="ButtonDisplaySize.Small">
                            <DropDownButtonItem v-for="commentType in compData.commentFilters" @click="saveCommentTypeFilterValue(commentType as CommentTypeFilterOptions)">
                                {{commentType}}
                                <MessageBadge :displayStyle="DisplayStyle.Info">
                                    {{commentTypeCount(commentType)}}
                                </MessageBadge>
                            </DropDownButtonItem>
    
                            <template #button>
                                <span class="me-1">
                                    <Icon :icon="IconTypes.Filter" :displayStyle="DisplayStyle.Light" class="me-1" />
                                    {{compData.existingComments.commentTypeListFilter}}
                                </span>
                            </template>
                        </DropDownButton>
                    </div>
                </div>
            </div>
    
            <div class="col-sm-12">
                <!-- Existing Comments -->
                <ChoiceTable 
                    :columns="compData.existingComments.columns"
                    fontSize="smaller"
                    :data="visibleExistingComments">    
                    <template #cell="{col, row, value}">
                        <template v-if="col.ExtraData?.editButton">
                            <button class="btn btn-primary btn-sm" @click="onEditComment(row as IExistingCommentModel)" title="Edit comment">
                                <Icon :icon="IconTypes.Edit" :displayStyle="DisplayStyle.Light"/>
                            </button>
                        </template>
                        <template v-if="'Comment' === col.Name">
                            {{ value }}

                            <div v-if="row.alert">
                                <MessageBadge title="There is an alert for this comment">
                                    <Icon :icon="IconTypes.Notification" :displayStyle="DisplayStyle.Warning"/>
                                    Alert
                                </MessageBadge>
                            </div>

                            <div v-if="userAndGroupCount(<IExistingCommentModel>row)">
                                <MessageBadge v-if="userAndGroupCount(<IExistingCommentModel>row)?.userCount" title="Users to alert of this comment">
                                    <Icon :icon="IconTypes.User" :displayStyle="DisplayStyle.Warning" />
                                    {{ userAndGroupCount(<IExistingCommentModel>row)?.userCount }}x
                                </MessageBadge>
                                <MessageBadge v-if="userAndGroupCount(<IExistingCommentModel>row)?.userGroupCount" title="User Group to alert of this comment">
                                    <Icon :icon="IconTypes.Users" :displayStyle="DisplayStyle.Warning" />
                                    {{ userAndGroupCount(<IExistingCommentModel>row)?.userGroupCount }}x
                                </MessageBadge>                                
                            </div>

                            <div v-if="(billerActionName(<IExistingCommentModel>row))">
                                <MessageBadge title="Biller Action">
                                    <Icon :icon="IconTypes.Action" :displayStyle="DisplayStyle.Warning"/>
                                    {{  (billerActionName(<IExistingCommentModel>row)) }}
                                </MessageBadge>
                            </div>
                        </template>
                    </template>
                </ChoiceTable>                
            </div>
        </template>
    </FoldingPanel>

    <template v-if="compData.commentEditorInfo.show">
        <StickyHeader>
            <template #sticky>
                <div class="bg-info ps-2 pe-2 pt-2 pb-2 me-4" style="height:3rem">
                    <Icon :icon="IconTypes.Comment" :displayStyle="DisplayStyle.Light" />
                    {{ commentCardTitle }}
                    <div class="float-end mb-1">
                        <Icon :icon="IconTypes.Save" 
                                v-if="enableCommentEditing"
                                :displayStyle="DisplayStyle.Dark" 
                                title="Save (Ctrl+S)" 
                                @click="saveComment" 
                                :hoverCursor="HoverCursorTypes.Pointer" 
                                class="me-2"/>
                        <Icon :icon="IconTypes.Close" 
                                :displayStyle="DisplayStyle.Dark" 
                                @click="showExistingComments()" 
                                title="Close Comment Editor (Esc)"
                                :hoverCursor="HoverCursorTypes.Pointer" />
                    </div>
                </div>
            </template>
        </StickyHeader>    
        <Card v-if="compData.commentEditorInfo.show">
            <template #title>
                <Icon :icon="IconTypes.Comment" :displayStyle="DisplayStyle.Light" />
                {{ commentCardTitle }}
                <span class="float-end">
                    <Icon :icon="IconTypes.Close" 
                            :displayStyle="DisplayStyle.Light" 
                            title="Close Comment Editor (Esc)" 
                            @click="showExistingComments()"
                            :hoverCursor="HoverCursorTypes.Pointer" />
                </span>
            </template>
            
            <div class="row">
                <div class="col-sm-12">
                    <div v-if="compData.commentEditorInfo.comment_id">
                        By: <UserEntryDate :userName="compData.commentEditorInfo.userName" :entryDate="compData.commentEditorInfo.entryDate" />
                    </div>
        
                    <div class="mt-2">
                        <div class="mb-1">
                            <TextInput   
                                ref="commentTextInput"
                                :rows="5" 
                                placeholder="New Comment"
                                :disabled="!enableCommentEditing"
                                :maxLength="8000"
                                v-model:text-value="compData.commentEditorInfo.comment" />
                        </div>
        
                        <Privilege :screen="AppScreens.DefaultComments" :privilege="PrivilegeType.ReadUpdate" v-if="!isSystemComment" >
                            <FoldingPanel v-model:panelOpen="compData.defaultCommentsPanelOpen">
                                <template #title>
                                    Default Comments                    
                                </template>
                                <template #body>
                                    <DefaultCommentsTable :initialConstantGroupId="compData.currentConstantDefaultCommentGroupId"
                                        :showClaimCommentParts="isClaimComment || isProcedureComment"
                                        @selected="addSelectedDefaultComment">
                                    </DefaultCommentsTable>
                                </template>
                            </FoldingPanel>
                        </Privilege>
                    </div>
        
                    <template v-if="isClaimComment || isProcedureComment">
                        <!-- IS REQUIRED -->
                        <TitledRow :title="billerActionLabel" class="mt-2">
                            <div class="input-group mb-1">
                                <select v-model="compData.commentEditorInfo.billerActionId" class="form-select" @change="ChangeBillerAction"
                                    :required="isBillerActionRequired"
                                    :disabled="!(privilegesStore.screens[AppScreens.BillerAction] >= PrivilegeType.Read)">
                                    <option :value="undefined"></option>
                                    <option v-for="opt in _.orderBy(visibleBillingActionOptions, i => i.description)" :value="opt.biller_action_id">
                                        {{ opt.description }}
                                        <template v-if="!opt.active"> (Inactive)</template>
                                    </option>
                                </select>
                                <div class="input-group-append ms-1" v-if="privilegesStore.screens[AppScreens.BillerAction] >= PrivilegeType.ReadUpdate">
                                    <button class="btn btn-primary" title="Edit/Add biller action options" @click="showBillerActionEditor">
                                        <Icon :icon="IconTypes.Edit" :displayStyle="DisplayStyle.Light" />
                                    </button>
                                </div>
                            </div>  
                            <div v-if="!billerActionReqsMet" class="red-text mb-1">Required</div>                       
                        </TitledRow>
                
                        <TitledRow :title="followUpLabel" class="mt-2">
                            <DatePickerNoTZ 
                                v-model:value="compData.commentEditorInfo.followupDate" 
                                placeholder="Followup Date" 
                                :disabled="!enableCommentEditing" />
                                <div v-if="!followupDateReqsMet" class="red-text mt-0 mb-1">Required</div>    
                        </TitledRow>

                        <TitledRow :title="assignedToLabel" class="mt-2">
                            <v-select 
                                v-model="compData.commentEditorInfo.selectedAssignedTo"
                                :options="compData.commentEditorInfo.usersAndGroupsList" 
                                :reduce="item => item" label="name" 
                                append-to-body
                                :select-on-tab="true">
                                    <template v-slot:option="option">
                                        <span v-if="DatabaseIdTypes.user_id === option.idType"><Icon :icon="IconTypes.User"></Icon></span>
                                        <span v-if="DatabaseIdTypes.user_group_id === option.idType"><Icon :icon="IconTypes.Users"></Icon></span>
                                        {{ option.name }}
                                    </template>
                                    <template #selected-option="{ idType, name }">
                                        <div>
                                            <span v-if="DatabaseIdTypes.user_id === idType"><Icon :icon="IconTypes.User"></Icon></span>
                                            <span v-if="DatabaseIdTypes.user_group_id === idType"><Icon :icon="IconTypes.Users"></Icon></span>
                                            <span class="ps-1">{{ name }}</span>
                                        </div>
                                    </template>
                            </v-select>
                        </TitledRow>   
                        <TitledRow class="mt-1" v-if="!assignedToReqsMet">
                            <div></div>
                            <div class="red-text">Required</div> 
                        </TitledRow>         
                        
                        <TitledRow title="Biller Action Completed" class="mt-3">
                            <div v-if="!compData.commentEditorInfo.billerActionCompletedDate">
                                <Checkbox v-model="compData.commentEditorInfo.billerActionCompleted" />
                            </div>
                            <div v-else>
                                {{ toDisplayDateString(compData.commentEditorInfo.billerActionCompletedDate, DateDisplayFormat.NumericDate) }}
                            </div>
                        </TitledRow>  
                        
                        <TitledRow v-if="isClaimComment" title="Include claim comment in statement" class="mt-3">
                            <Checkbox v-model="compData.commentEditorInfo.includeOnStatement" />
                        </TitledRow>  
                    </template>
        
                    <hr/>
                    
                    <div v-if="isPatientComment">
                        <FoldingPanel v-model:panel-open="compData.commentEditorInfo.alertOptionsPanelOpen">
                            <template #title>Alert</template>                            
                            <template #body>
                                <TitledRow title="Effective Date">
                                    <DatePickerNoTZ 
                                        v-model:value="compData.commentEditorInfo.effectiveDate"
                                        placeholder="Effective Date"
                                        :disabled="!enableCommentEditing" />
                                </TitledRow>
                                
                                <TitledRow title="Expiration Date">   
                                    <DatePickerNoTZ 
                                        v-model:value="compData.commentEditorInfo.expirationDate"
                                        placeholder="Expiration Date" 
                                        :disabled="!enableCommentEditing" />
                                </TitledRow>
        
                                <!-- Alert indicates where a popup shows in the UI -->
                                <OptionsDropPanel :isOpen="compData.commentEditorInfo.openAlertOptionsTree">
                                    <template #title>Alert</template>
                                    <template #options>
                                        <AlertsTree 
                                            :alertOptions="compData.alertOptions"
                                            :selectedAlertOptions="Array.from(compData.commentEditorInfo.selectedAlertOptions.values())"
                                            @update:selectedAlertOptions="val => compData.commentEditorInfo.selectedAlertOptions = val" />
                                        <hr/>
                                    </template>
                                    <template #summary>
                                        <SelectedAlertBadges :alertIds="Array.from(compData.commentEditorInfo.selectedAlertOptions)" />
                                    </template>
                                </OptionsDropPanel>
        
                                <OptionsDropPanel>
                                    <template #title>Users/Groups</template>
                                    <template #options>
                                            <UsersAndGroupsTable 
                                                :usersAndGroups="compData.usersAndGroups"
                                                v-model:selectedusersAndGroupsIds="compData.commentEditorInfo.selectedUserOrGroupDatabaseIds"/>
                                        <hr/>
                                    </template>
                                    <template #summary>
                                        <SelectedUsersAndGroupBadges 
                                            :usersAndGroups="compData.usersAndGroups"
                                            :selectedUsersAndGroups="compData.commentEditorInfo.selectedUserOrGroupDatabaseIds"
                                            :nothingSelectedText="'All'" />
                                    </template>
                                </OptionsDropPanel>
                            </template>
                        </FoldingPanel>
                        <TitledRow title="Include patient comment in statement" class="mt-3">
                            <Checkbox v-model="compData.commentEditorInfo.includeOnStatement" />
                        </TitledRow>  
                    </div>
        
                    <div class="mt-2" v-if="enableCommentEditing">
                        <button class="btn btn-success" @click="saveComment" v-if="validNewComment" title="Save (Ctrl+S)" :disabled="compData.commentEditorInfo.savingComment ?? false">
                            <LoadingSection loadingMsg="" :isLoading="compData.commentEditorInfo.savingComment" />
                            <ModifierKeyMonitor>
                                <template #keydown="{ctrl}">
                                    <span v-if="ctrl">
                                        <u>S</u>ave
                                    </span>
                                    <span v-else>
                                        Save
                                    </span>
                                </template>
                            </ModifierKeyMonitor>
                        </button>
                        <MessageSection v-if="addNewCommentProblemText" :type="MessageSectionType.Row" :displayStyle="DisplayStyle.Warning">
                            {{ addNewCommentProblemText }}
                        </MessageSection>
                    </div> 

                </div>
            </div>
        </Card>
    </template>

    <ModalDialog :show="compData.promptForMessageActionDialog.show">
        <template #title>Use message?</template>

        <div>
            Would you like to use the message you just added?
        </div>
        <div class="alert alert-light ms-2 me-2">
            <em>{{ compData.promptForMessageActionDialog.comment?.commentText ?? "(no comment)" }}</em>
        </div>

        <template #footer>
            <button class="btn btn-danger" @click="compData.promptForMessageActionDialog.show = false">
                No
            </button>
            <button class="btn btn-secondary" 
                    @click="addSelectedDefaultComment(compData.promptForMessageActionDialog.comment, true); compData.promptForMessageActionDialog.show = false;"
                    title="Replace the existing comment with the new comment">
                <Icon :icon="IconTypes.TurnLeft" :displayStyle="DisplayStyle.Light" />
                Replace
            </button>

            <button class="btn btn-success" 
                    @click="addSelectedDefaultComment(compData.promptForMessageActionDialog.comment, false); compData.promptForMessageActionDialog.show = false;"
                    title="Append the new comment to the end of the existing comment">
                <Icon :icon="IconTypes.Add" :displayStyle="DisplayStyle.Light" />
                Append
            </button>
        </template>
    </ModalDialog>

    <ChoiceStateMessageSink :channel-name="MessageChannelName.CommentsEditor" @message="sinkCommentActionMessage"/>

    <Teleport to="title">
        {{compTitle}}
    </Teleport>
</div>
</template>